/**
 * Notes d'ÉQUIPE par match (moyenne des notes joueurs pondérée par les minutes,
 * calculée côté serveur — cf. server/teamRating.mjs). Deux ressources :
 *   - index global /data/team-ratings.json (préchargé) → note d'équipe d'un match
 *     pour l'affichage autour du score (lookup SYNCHRONE après préchargement) ;
 *   - /data/team-ratings/<CODE>.json → évolution d'un pays match par match.
 * Échec réseau ⇒ null, l'app reste intacte (règle 8).
 */
export interface TeamRatingEntry {
  readonly rating: number;
  readonly n: number; // nb de joueurs notés pris en compte
}
export interface TeamRatingHistoryRow {
  readonly matchId: number;
  readonly oppCode: string | null;
  readonly rating: number | null;
  readonly n: number;
}
export interface TeamRatingHistory {
  readonly code: string;
  readonly matches: readonly TeamRatingHistoryRow[];
}

export type Trend = "up" | "down" | "flat";

/**
 * Tendance d'une série de notes : pente de la régression linéaire, seuillée.
 * "up" = en hausse, "down" = en baisse, "flat" = stable. < 2 points → stable.
 * (Pour 2 points, la pente vaut exactement la différence des deux notes.)
 */
export function ratingTrend(ratings: readonly (number | null)[]): Trend {
  const ys = ratings.filter((r): r is number => r != null && Number.isFinite(r));
  if (ys.length < 2) return "flat";
  const n = ys.length;
  const xMean = (n - 1) / 2;
  const yMean = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  ys.forEach((y, i) => {
    num += (i - xMean) * (y - yMean);
    den += (i - xMean) ** 2;
  });
  const slope = den === 0 ? 0 : num / den; // points de note par match
  if (slope > 0.15) return "up";
  if (slope < -0.15) return "down";
  return "flat";
}

type Index = Record<number, Record<string, TeamRatingEntry>>;

let index: Index | null = null;
let indexPending: Promise<Index | null> | null = null;

function loadIndex(): Promise<Index | null> {
  if (index) return Promise.resolve(index);
  indexPending ??= fetch("data/team-ratings.json")
    .then((r) => (r.ok ? (r.json() as Promise<{ byMatch?: Index }>) : null))
    .then((doc) => {
      index = doc?.byMatch ?? null;
      return index;
    })
    .catch(() => null);
  return indexPending;
}

/** Précharge l'index pour que la note soit dispo dès la 1re carte de match. */
export function preloadTeamRatings(): void {
  void loadIndex();
}

/** Promesse résolue quand l'index des notes d'équipe est chargé (ou indisponible). */
export function ensureTeamRatings(): Promise<void> {
  return loadIndex().then(() => undefined);
}

/**
 * FORME récente de chaque équipe = moyenne de ses dernières notes d'équipe
 * (jusqu'aux 3 derniers matchs), pondérée vers le plus récent (poids 1·2·3).
 * Lue depuis l'index préchargé ; null si pas encore chargé.
 */
export function teamFormRatings(): Record<string, number> | null {
  if (!index) return null;
  const series: Record<string, { mid: number; r: number }[]> = {};
  for (const [mid, byTeam] of Object.entries(index)) {
    for (const [code, e] of Object.entries(byTeam)) {
      if (e?.rating != null) (series[code] ??= []).push({ mid: Number(mid), r: e.rating });
    }
  }
  const out: Record<string, number> = {};
  for (const [code, arr] of Object.entries(series)) {
    arr.sort((a, b) => a.mid - b.mid);
    const recent = arr.slice(-3);
    let num = 0;
    let den = 0;
    recent.forEach((p, i) => {
      const w = i + 1; // le plus récent pèse le plus
      num += w * p.r;
      den += w;
    });
    if (den > 0) out[code] = num / den;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Note d'équipe MOYENNE de chaque équipe sur TOUT le tournoi (moyenne simple de ses
 * notes d'équipe par match), depuis l'index préchargé. `{ avg, n }` par code ; `{}`
 * si l'index n'est pas encore chargé. Sert la colonne « Note » du classement Elo.
 */
export function teamTournamentRatings(): Record<string, { avg: number; n: number }> {
  if (!index) return {};
  const acc: Record<string, { sum: number; n: number }> = {};
  for (const byTeam of Object.values(index)) {
    for (const [code, e] of Object.entries(byTeam)) {
      if (e?.rating == null) continue;
      const a = (acc[code] ??= { sum: 0, n: 0 });
      a.sum += e.rating;
      a.n += 1;
    }
  }
  const out: Record<string, { avg: number; n: number }> = {};
  for (const [code, { sum, n }] of Object.entries(acc)) out[code] = { avg: sum / n, n };
  return out;
}

/**
 * Tendance de FORME d'une équipe (depuis l'index préchargé) :
 *  - ≥ 2 matchs notés → pente de ses ~4 dernières notes (vraie tendance) ;
 *  - 1 seul match noté → pas de pente possible, on situe la note vs la MOYENNE du
 *    tournoi (au-dessus = up, en-dessous = down) → la flèche apparaît dès le 2e match ;
 *  - 0 match noté → null (pas de flèche).
 */
export function teamTrendOf(code: string): Trend | null {
  if (!index) return null;
  const series: { mid: number; r: number }[] = [];
  let fieldSum = 0;
  let fieldN = 0;
  for (const [mid, byTeam] of Object.entries(index)) {
    for (const [c, e] of Object.entries(byTeam)) {
      if (e?.rating == null) continue;
      fieldSum += e.rating;
      fieldN += 1;
      if (c === code) series.push({ mid: Number(mid), r: e.rating });
    }
  }
  if (series.length === 0) return null;
  if (series.length === 1) {
    // 1 seul match noté : pas de pente → situe la note vs la moyenne du tournoi.
    const mu = fieldN ? fieldSum / fieldN : 6.8;
    const r = series[0]?.r ?? mu;
    return r > mu + 0.25 ? "up" : r < mu - 0.25 ? "down" : "flat";
  }
  series.sort((a, b) => a.mid - b.mid);
  return ratingTrend(series.slice(-4).map((p) => p.r));
}

/**
 * Ajuste une table d'Elo par la FORME (note d'équipe récente) — EXPÉRIMENTAL :
 * pour chaque équipe, Elo += clamp(k·(forme − moyenne), ±cap). Pondère le talent
 * brut (Elo+valeur) par la performance récente, mais PLAFONNÉ pour ne jamais
 * dominer. Pur et testé. La moyenne est calculée sur les équipes ayant une forme.
 */
export function applyFormFactor(
  elos: Readonly<Record<string, number>>,
  form: Readonly<Record<string, number>>,
  k: number,
  cap: number,
): Record<string, number> {
  const codes = Object.keys(form);
  const out: Record<string, number> = { ...elos };
  if (!codes.length) return out;
  const mu = codes.reduce((a, c) => a + (form[c] ?? 0), 0) / codes.length;
  for (const c of codes) {
    if (out[c] == null) continue;
    out[c] += Math.max(-cap, Math.min(cap, k * ((form[c] ?? mu) - mu)));
  }
  return out;
}

/**
 * Pénalité d'ABSENTS (blessés/suspendus) — EXPÉRIMENTAL : Elo −= min(cap, k·poids).
 * SOUSTRACTIVE et bornée — une absence est ABSOLUE (jamais de bonus), contrairement
 * à la forme qui est relative à la moyenne du champ. Pure et testée.
 */
export function applyAbsenceFactor(
  elos: Readonly<Record<string, number>>,
  weight: Readonly<Record<string, number>>,
  k: number,
  cap: number,
): Record<string, number> {
  const out: Record<string, number> = { ...elos };
  for (const [c, w] of Object.entries(weight)) {
    if (out[c] == null) continue;
    out[c] -= Math.min(cap, k * Math.max(0, w));
  }
  return out;
}

/** Note d'équipe d'un match (depuis le cache préchargé), ou null si absente. */
export function teamRatingOf(matchId: number, teamCode: string): TeamRatingEntry | null {
  return index?.[matchId]?.[teamCode] ?? null;
}

const histCache = new Map<string, TeamRatingHistory | null>();
const histPending = new Map<string, Promise<TeamRatingHistory | null>>();

/** Évolution de la note d'équipe d'un pays (mise en cache par code), ou null. */
export async function fetchTeamRatingHistory(code: string): Promise<TeamRatingHistory | null> {
  if (histCache.has(code)) return histCache.get(code) ?? null;
  const inflight = histPending.get(code);
  if (inflight) return inflight;
  const p = fetch(`data/team-ratings/${code}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<TeamRatingHistory>) : null))
    .then((doc) => {
      const val = doc && Array.isArray(doc.matches) ? doc : null;
      histCache.set(code, val);
      return val;
    })
    .catch(() => null)
    .finally(() => {
      histPending.delete(code);
    });
  histPending.set(code, p);
  return p;
}
